<template>
  <div class="introduction-container">
    <h1 class="main-title">遥感数据大气校正智能处理平台</h1>
    <p class="sub-title">
      <span class="highlight">国家级遥感数据处理平台</span>
      融合中科院20年遥感研究成果，提供从原始数据到专业成果的一站式解决方案。<br>
      基于先进的辐射传输模型，融合卫星基础信息库和大气知识库，支持多种卫星、多种大气条件下的自动大气校正和反射率反演，适用于农业、生态、环境、资源等领域的遥感定量应用
    </p>


    <div class="main-content">
      <!-- 左侧软件按钮 -->
      <div class="software-sidebar">
        <h3 class="sidebar-title">软件类型</h3>
        <div class="software-buttons">
          <button 
            v-for="software in softwareList" 
            :key="software.id"
            :class="['software-btn', { active: currentSoftware === software.id }]"
            @click="switchSoftware(software.id)"
          >
            {{ software.name }}
          </button>
        </div>
      </div>

      <!-- 右侧内容区域 -->
      <div class="content-area">
        <div v-if="currentSoftware === 1" class="software-content">
          <!-- GAC_MSS 内容 -->
          <h2 class="section-title">GAC_MSS 国产陆地卫星辐射校正软件</h2>
          
          <!-- 软件适用卫星类型 -->
          <div class="satellite-catalog">
            <h3 class="subsection-title">适用的国产陆地卫星及载荷表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>卫星名称</th>
                  <th>载荷名称</th>
                  <th>载荷说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>GF1</td><td>PMS1/PMS2</td><td>2 米/8 米全色多光谱相机<br>WFV1/WFV2/WFV3/WFV4 16 米多光谱相机</td></tr>
                <tr><td>2</td><td>GF2</td><td>PMS1/PMS2</td><td>0.8 米/3.2 米全色多光谱相机</td></tr>
                <tr><td>3</td><td>GF6</td><td>PMS / WFV</td><td>2 米/8 米全色多光谱相机<br>WFV 16 米多光谱相机</td></tr>
                <tr><td>4</td><td>GF7</td><td>PMS</td><td>0.65 米/2.6 米全色多光谱相机</td></tr>
                <tr><td>5</td><td>GF1B</td><td>PMS</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>6</td><td>GF1C</td><td>PMS</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>7</td><td>GF1D</td><td>PMS</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>8</td><td>GFDM</td><td>PMS</td><td>0.42 米/1.68 米全色多光谱相机</td></tr>
                <tr><td>9</td><td>HJ1A</td><td>CCD1/CCD2</td><td>30 米多光谱相机</td></tr>
                <tr><td>10</td><td>HJ1B</td><td>CCD1/CCD2</td><td>30 米多光谱相机</td></tr>
                <tr><td>11</td><td>HJ2A</td><td>CCD1/CCD2/CCD3/CCD4</td><td>16 米多光谱相机</td></tr>
                <tr><td>12</td><td>HJ2B</td><td>CCD1/CCD2/CCD3/CCD4</td><td>16 米多光谱相机</td></tr>
                <tr><td>13</td><td>ZY1E</td><td>VNIC</td><td>2.5 米/10 米全色多光谱相机</td></tr>
                <tr><td>14</td><td>ZY1F</td><td>VNIC</td><td>2.5 米/10 米全色多光谱相机</td></tr>
                <tr><td>15</td><td>ZY02C</td><td>PMS</td><td>5 米/10 米全色多光谱相机</td></tr>
                <tr><td>16</td><td>ZY3</td><td>MUX / NAD</td><td>5.8 米多光谱相机<br>NAD 2.1 米正视全色相机</td></tr>
                <tr><td>17</td><td>ZY302</td><td>MUX / NAD</td><td>5.8 米多光谱相机<br>NAD 2.1 米正视全色相机</td></tr>
                <tr><td>18</td><td>ZY303</td><td>MUX / NAD</td><td>6 米多光谱相机<br>NAD 2.1 米正视全色相机</td></tr>
                <tr><td>19</td><td>CBERS02</td><td>CCD</td><td>19.5 米多光谱相机</td></tr>
                <tr><td>20</td><td>CBERS02B</td><td>CCD</td><td>20 米多光谱相机</td></tr>
                <tr><td>21</td><td>CB04</td><td>WFI / MUX / PMS</td><td>73 米宽幅多光谱相机<br>20 米多光谱相机<br>5 米/10 米全色多光谱相机</td></tr>
                <tr><td>22</td><td>CB04A</td><td>WFI / MUX / WPM</td><td>60 米宽幅多光谱相机<br>17 米多光谱相机<br>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>合计</td><td>22 颗卫星</td><td>44 个相机</td><td>59 类影像</td></tr>
              
              </tbody>
            </table>
            <!-- <div class="mb-8">
              <h4 class="text-lg font-semibold text-green-700 mb-4">🛰️ 高分系列卫星</h4>
              <table class="satellite-table">
                <thead>
                  <tr>
                    <th>卫星/载荷</th><th>发射日期</th><th>轨道高度</th><th>载荷名称</th><th>波段数</th><th>空间分辨率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>高分一号</td><td>2013.04.26</td><td>645 km</td><td>GF1_PMS1</td><td>4</td><td>8 m</td></tr>
                  <tr><td></td><td>2013.04.26</td><td>645 km</td><td>GF1_PMS2</td><td>4</td><td>8 m</td></tr>
                  <tr><td></td><td>2013.04.26</td><td>645 km</td><td>GF1_PAN1</td><td>1</td><td>2 m</td></tr>
                  <tr><td></td><td>2013.04.26</td><td>645 km</td><td>GF1_PAN2</td><td>1</td><td>2 m</td></tr>
                  <tr><td></td><td>2013.04.26</td><td>645 km</td><td>GF1_WFV1</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2013.04.26</td><td>645 km</td><td>GF1_WFV2</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2013.04.26</td><td>645 km</td><td>GF1_WFV3</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2013.04.26</td><td>645 km</td><td>GF1_WFV4</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2015.10.19</td><td>645 km</td><td>GF1B_PMS</td><td>4</td><td>8 m</td></tr>
                  <tr><td></td><td>2016.11.23</td><td>645 km</td><td>GF1C_PMS</td><td>4</td><td>8 m</td></tr>
                  <tr><td></td><td>2017.10.06</td><td>645 km</td><td>GF1D_PMS</td><td>4</td><td>8 m</td></tr>
                  <tr><td>高分二号</td><td>2014.08.19</td><td>630 km</td><td>GF2_PMS1</td><td>4</td><td>3.2 m</td></tr>
                  <tr><td></td><td>2014.08.19</td><td>630 km</td><td>GF2_PMS2</td><td>4</td><td>3.2 m</td></tr>
                  <tr><td></td><td>2014.08.19</td><td>630 km</td><td>GF2_PAN1</td><td>1</td><td>0.8 m</td></tr>
                  <tr><td></td><td>2014.08.19</td><td>630 km</td><td>GF2_PAN2</td><td>1</td><td>0.8 m</td></tr>
                  <tr><td>高分五号</td><td>2021.09.07</td><td>705 km</td><td>GF5B_VIMI</td><td>≥4</td><td>20 m</td></tr>
                  <tr><td>高分六号</td><td>2018.06.02</td><td>645 km</td><td>GF6_PMS</td><td>4</td><td>8 m</td></tr>
                  <tr><td></td><td>2018.06.02</td><td>645 km</td><td>GF6_PAN</td><td>1</td><td>2 m</td></tr>
                  <tr><td></td><td>2018.06.02</td><td>645 km</td><td>GF6_WFV</td><td>4</td><td>16 m</td></tr>
                  <tr><td>高分七号</td><td>2019.11.03</td><td>500 km</td><td>GF7_MUX</td><td>≥4</td><td>3.2 m</td></tr>
                  <tr><td></td><td>2019.11.03</td><td>500 km</td><td>GF7_PAN</td><td>1</td><td>0.8 m</td></tr>
                  <tr><td>高分多模</td><td>2020</td><td>500 km</td><td>GFDM_PAN</td><td>1</td><td>0.5 m</td></tr>
                  <tr><td></td><td>2020</td><td>500 km</td><td>GFDM_MSS</td><td>≥4</td><td>2 m</td></tr>
                </tbody>
              </table>
            </div>
            <div class="mb-8">
              <h4 class="text-lg font-semibold text-green-700 mb-4">🛰️ 资源系列卫星</h4>
              <table class="satellite-table">
                <thead>
                  <tr>
                    <th>卫星/载荷</th><th>发射日期</th><th>轨道高度</th><th>载荷名称</th><th>波段数</th><th>空间分辨率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>资源一号</td><td>2019.09.12</td><td>778 km</td><td>ZY1E_VNIC</td><td>≥4</td><td>10 m</td></tr>
                  <tr><td></td><td>2021.12.26</td><td>778 km</td><td>ZY1F_VNIC</td><td>≥4</td><td>10 m</td></tr>
                  <tr><td></td><td>2019.09.12</td><td>778 km</td><td>ZY1E_PAN</td><td>1</td><td>2.5 m</td></tr>
                  <tr><td></td><td>2021.12.26</td><td>778 km</td><td>ZY1F_PAN</td><td>1</td><td>2.5 m</td></tr>
                  <tr><td>资源二号</td><td>2020.04.25</td><td>505 km</td><td>ZY02C_PAN</td><td>1</td><td>5 m</td></tr>
                  <tr><td></td><td>2020.04.25</td><td>505 km</td><td>ZY02C_PMS</td><td>≥4</td><td>10 m</td></tr>
                  <tr><td>资源三号</td><td>2012.01.09</td><td>505 km</td><td>ZY3_MUX</td><td>≥4</td><td>6 m</td></tr>
                  <tr><td></td><td>2016.05.30</td><td>505 km</td><td>ZY302_MUX</td><td>≥4</td><td>6 m</td></tr>
                  <tr><td></td><td>2020.04.25</td><td>505 km</td><td>ZY303_MUX</td><td>≥4</td><td>6 m</td></tr>
                </tbody>
              </table>
            </div>
            <div class="mb-8">
              <h4 class="text-lg font-semibold text-green-700 mb-4">🌏 中巴资源卫星</h4>
              <table class="satellite-table">
                <thead>
                  <tr>
                    <th>卫星/载荷</th><th>发射日期</th><th>轨道高度</th><th>载荷名称</th><th>波段数</th><th>空间分辨率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>中巴地球04A</td><td>2014.12.07</td><td>778 km</td><td>CB04A_MUX</td><td>≥4</td><td>17 m</td></tr>
                  <tr><td></td><td>2014.12.07</td><td>778 km</td><td>CB04A_WFI</td><td>≥4</td><td>60 m</td></tr>
                  <tr><td></td><td>2014.12.07</td><td>778 km</td><td>CB04A_WPM</td><td>≥4</td><td>8 m</td></tr>
                  <tr><td>中巴地球02</td><td>2003.10.21</td><td>778 km</td><td>CBERS02_CCD</td><td>≥4</td><td>19.5 m</td></tr>
                  <tr><td>中巴地球02B</td><td>2007.09.19</td><td>778 km</td><td>CBERS2B_CCD</td><td>≥4</td><td>19.5 m</td></tr>
                </tbody>
              </table>
            </div>
            <div class="mb-8">
              <h4 class="text-lg font-semibold text-green-700 mb-4">🌪️ 环境与灾害监测卫星</h4>
              <table class="satellite-table">
                <thead>
                  <tr>
                    <th>卫星/载荷</th><th>发射日期</th><th>轨道高度</th><th>载荷名称</th><th>波段数</th><th>空间分辨率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>环境一号</td><td>2008.09.06</td><td>649 km</td><td>HJ1A_CCD1</td><td>4</td><td>30 m</td></tr>
                  <tr><td></td><td>2008.09.06</td><td>649 km</td><td>HJ1A_CCD2</td><td>4</td><td>30 m</td></tr>
                  <tr><td></td><td>2008.09.06</td><td>649 km</td><td>HJ1B_CCD1</td><td>4</td><td>30 m</td></tr>
                  <tr><td></td><td>2008.09.06</td><td>649 km</td><td>HJ1B_CCD2</td><td>4</td><td>30 m</td></tr>
                  <tr><td>环境二号</td><td>2020.09.27</td><td>650 km</td><td>HJ2A_CCD1</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2020.09.27</td><td>650 km</td><td>HJ2A_CCD2</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2020.09.27</td><td>650 km</td><td>HJ2A_CCD3</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2020.09.27</td><td>650 km</td><td>HJ2A_CCD4</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2020.09.27</td><td>650 km</td><td>HJ2B_CCD1</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2020.09.27</td><td>650 km</td><td>HJ2B_CCD2</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2020.09.27</td><td>650 km</td><td>HJ2B_CCD3</td><td>4</td><td>16 m</td></tr>
                  <tr><td></td><td>2020.09.27</td><td>650 km</td><td>HJ2B_CCD4</td><td>4</td><td>16 m</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <h4 class="text-lg font-semibold text-green-700 mb-4">📡 商业遥感卫星</h4>
              <table class="satellite-table">
                <thead>
                  <tr>
                    <th>卫星/载荷</th><th>发射日期</th><th>轨道高度</th><th>载荷名称</th><th>波段数</th><th>空间分辨率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>陆地生态碳CM1</td><td>2022.08.04</td><td>705 km</td><td>CM1_DMC_B1</td><td>≥4</td><td>6 m</td></tr>
                  <tr><td></td><td>2022.08.04</td><td>705 km</td><td>CM1_DMC_B2</td><td>≥4</td><td>6 m</td></tr>
                  <tr><td></td><td>2022.08.04</td><td>705 km</td><td>CM1_DMC_F1</td><td>≥4</td><td>4 m</td></tr>
                  <tr><td></td><td>2022.08.04</td><td>705 km</td><td>CM1_DMC_F2</td><td>≥4</td><td>4 m</td></tr>
                  <tr><td></td><td>2022.08.04</td><td>705 km</td><td>CM1_DMC_NAD</td><td>≥4</td><td>2 m</td></tr>
                </tbody>
              </table>
            </div> -->
          </div>

          <!-- 软件功能组成表（GAC_MSS） -->
          <div class="software-features">
            <h3 class="subsection-title">软件功能组成表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>功能说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td>根据卫星载荷类型，将灰度值图像转换成辐亮度图像</td></tr>
                <tr><td>2</td><td>大气校正</td><td>利用自研辐射传输模型，得到全色或多光谱地表反射率影像</td></tr>
                <tr><td>3</td><td>几何校正</td><td>利用 RPC 文件，实现地表反射率图像的系统几何校正</td></tr>
                <tr><td>4</td><td>一站式处理</td><td>实现从辐射定标-大气校正-几何校正的一站式处理</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件使用要求（GAC_MSS） -->
          <div class="software-requirements">
            <h3 class="subsection-title">软件使用要求</h3>
            <div class="requirement-item">
              <ul>
                <li>该软件为免费使用软件，需要实名注册后即可使用。</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 其他软件内容 -->
        <div v-if="currentSoftware === 2" class="software-content">
          <h2 class="section-title">GAC_PMS 国产全色多光谱卫星辐射校正软件</h2>
          <div class="satellite-catalog">
            <h3 class="subsection-title">适用的国产陆地卫星及载荷表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>卫星名称</th>
                  <th>载荷名称</th>
                  <th>载荷说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>GF1</td><td>PMS1/PMS2</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>2</td><td>GF2</td><td>PMS1/PMS2</td><td>0.8 米/3.2 米全色多光谱相机</td></tr>
                <tr><td>3</td><td>GF6</td><td>PMS</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>4</td><td>GF7</td><td>PMS</td><td>0.65 米/2.6 米全色多光谱相机</td></tr>
                <tr><td>5</td><td>GF1B</td><td>PMS</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>6</td><td>GF1C</td><td>PMS</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>7</td><td>GF1D</td><td>PMS</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>8</td><td>GFDM</td><td>PMS</td><td>0.42 米/1.68 米全色多光谱相机</td></tr>
                <tr><td>9</td><td>ZY1E</td><td>VNIC</td><td>2.5 米/10 米全色多光谱相机</td></tr>
                <tr><td>10</td><td>ZY1F</td><td>VNIC</td><td>2.5 米/10 米全色多光谱相机</td></tr>
                <tr><td>11</td><td>CB04A</td><td>WPM</td><td>2 米/8 米全色多光谱相机</td></tr>
                <tr><td>合计</td><td>11 颗卫星</td><td>13 个相机</td><td>36 类影像</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件功能组成表（GAC_PMS） -->
          <div class="software-features">
            <h3 class="subsection-title">软件功能组成表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>功能说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td>根据卫星载荷类型，将灰度值图像转换成辐亮度图像</td></tr>
                <tr><td>2</td><td>大气校正</td><td>利用自研辐射传输模型，得到全色或多光谱地表反射率影像</td></tr>
                <tr><td>3</td><td>几何校正</td><td>利用 RPC 文件，实现地表反射率图像的系统几何校正</td></tr>
                <tr><td>4</td><td>图像融合</td><td>实现全色和多光谱图像的有效快速融合</td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td>根据经纬度信息，实现多景影像的拼接</td></tr>
                <tr><td>6</td><td>一站式处理</td><td>实现从辐射定标-大气校正-几何校正-图像融合的一站式处理</td></tr>
                <tr><td>7</td><td>批处理</td><td>实现多景全色和多光谱影像的自动辐射校正批处理</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件使用要求（GAC_PMS） -->
          <div class="software-requirements">
            <h3 class="subsection-title">软件使用要求</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>收费说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td><span class="charge-free">免费使用</span></td></tr>
                <tr><td>2</td><td>大气校正</td><td><span class="charge-free">免费使用</span></td></tr>
                <tr><td>3</td><td>几何校正</td><td><span class="charge-free">免费使用</span></td></tr>
                <tr><td>4</td><td>图像融合</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>6</td><td>一站式处理</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>7</td><td>批处理</td><td><span class="charge-paid">企业版，需缴费使用</span></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="currentSoftware === 3" class="software-content">
          <h2 class="section-title">GAC_CCD 国产16米多光谱卫星辐射校正软件</h2>
          <div class="satellite-catalog">
            <h3 class="subsection-title">适用的国产陆地卫星及载荷表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>卫星名称</th>
                  <th>载荷名称</th>
                  <th>载荷说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>GF1</td><td>WFV1/WFV2/WFV3/WFV4</td><td>16 米多光谱相机</td></tr>
                <tr><td>2</td><td>GF6</td><td>WFV</td><td>16 米多光谱相机</td></tr>
                <tr><td>3</td><td>HJ2A</td><td>CCD1/CCD2/CCD3/CCD4</td><td>16 米多光谱相机</td></tr>
                <tr><td>4</td><td>HJ2B</td><td>CCD1/CCD2/CCD3/CCD4</td><td>16 米多光谱相机</td></tr>
                <tr><td>合计</td><td>4 颗卫星</td><td>13 个相机</td><td>13 类影像</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件功能组成表（GAC_CCD） -->
          <div class="software-features">
            <h3 class="subsection-title">软件功能组成表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>功能说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td>根据卫星载荷类型，将灰度值图像转换成辐亮度图像</td></tr>
                <tr><td>2</td><td>大气校正</td><td>利用自研辐射传输模型，得到全色或多光谱地表反射率影像</td></tr>
                <tr><td>3</td><td>几何校正</td><td>利用 RPC 文件，实现地表反射率图像的系统几何校正</td></tr>
                <tr><td>4</td><td>一站式处理</td><td>实现从辐射定标-大气校正-几何校正的一站式处理</td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td>根据经纬度信息，实现多景影像的拼接</td></tr>
                <tr><td>6</td><td>批处理</td><td>实现多景全色和多光谱影像的自动辐射校正批处理</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件使用要求（GAC_CCD） -->
          <div class="software-requirements">
            <h3 class="subsection-title">软件使用要求</h3>
            <p style="color: var(--text-secondary); margin-bottom: 0.5rem;">该软件为部分功能收费软件，需要实名注册后即可使用。软件收费说明如下：</p>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>收费说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td><span class="charge-free">免费使用</span></td></tr>
                <tr><td>2</td><td>大气校正</td><td><span class="charge-free">免费使用</span></td></tr>
                <tr><td>3</td><td>几何校正</td><td><span class="charge-free">免费使用</span></td></tr>
                <tr><td>4</td><td>一站式处理</td><td><span class="charge-free">免费使用</span></td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>6</td><td>批处理</td><td><span class="charge-paid">企业版，需缴费使用</span></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="currentSoftware === 4" class="software-content">
          <h2 class="section-title">GAC_AHSI_Water 国产高光谱卫星辐射校正软件（400-1000nm）</h2>
          <div class="satellite-catalog">
            <h3 class="subsection-title">适用的国产陆地卫星及载荷表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>卫星名称</th>
                  <th>载荷名称</th>
                  <th>载荷说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>GF5B</td><td>AHSI</td><td>30 米高光谱相机（150 波段，400-1000nm）</td></tr>
                <tr><td>2</td><td>GF501A</td><td>AHSI</td><td>30 米高光谱相机（150 波段，400-1000nm）</td></tr>
                <tr><td>3</td><td>ZY1E</td><td>AHSI</td><td>30 米高光谱相机（76 波段，400-1000nm）</td></tr>
                <tr><td>4</td><td>ZY1F</td><td>AHSI</td><td>30 米高光谱相机（76 波段，400-1000nm）</td></tr>
                <tr><td>合计</td><td>4 颗卫星</td><td>4 个相机</td><td>4 类影像</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件功能组成表（GAC_AHSI_Water） -->
          <div class="software-features">
            <h3 class="subsection-title">软件功能组成表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>功能说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td>根据卫星载荷类型，将灰度值图像转换成辐亮度图像</td></tr>
                <tr><td>2</td><td>大气校正</td><td>利用自研辐射传输模型，得到全色或多光谱地表反射率影像</td></tr>
                <tr><td>3</td><td>几何校正</td><td>利用 RPC 文件，实现地表反射率图像的系统几何校正</td></tr>
                <tr><td>4</td><td>一站式处理</td><td>实现从辐射定标-大气校正-几何校正的一站式处理</td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td>根据经纬度信息，实现多景影像的拼接</td></tr>
                <tr><td>6</td><td>批处理</td><td>实现多景全色和多光谱影像的自动辐射校正批处理</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件使用要求（GAC_AHSI_Water） -->
          <div class="software-requirements">
            <h3 class="subsection-title">软件使用要求</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>收费说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>2</td><td>大气校正</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>3</td><td>几何校正</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>4</td><td>一站式处理</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>6</td><td>批处理</td><td><span class="charge-paid">企业版，需缴费使用</span></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="currentSoftware === 5" class="software-content">
          <h2 class="section-title">GAC_AHSI_Mineral 国产高光谱卫星辐射校正软件（400-2500nm）</h2>
          <div class="satellite-catalog">
            <h3 class="subsection-title">适用的国产陆地卫星及载荷表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>卫星名称</th>
                  <th>载荷名称</th>
                  <th>载荷说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>GF5B</td><td>AHSI</td><td>30 米高光谱相机（330 波段，400-2500nm）</td></tr>
                <tr><td>2</td><td>GF501A</td><td>AHSI</td><td>30 米高光谱相机（330 波段，400-2500nm）</td></tr>
                <tr><td>3</td><td>ZY1E</td><td>AHSI</td><td>30 米高光谱相机（166 波段，400-2500nm）</td></tr>
                <tr><td>4</td><td>ZY1F</td><td>AHSI</td><td>30 米高光谱相机（166 波段，400-2500nm）</td></tr>
                <tr><td>合计</td><td>4 颗卫星</td><td>4 个相机</td><td>4 类影像</td></tr>   
              </tbody>
            </table>
          </div>

          <!-- 软件功能组成表（GAC_AHSI_Mineral） -->
          <div class="software-features">
            <h3 class="subsection-title">软件功能组成表</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>功能说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td>根据卫星载荷类型，将灰度值图像转换成辐亮度图像</td></tr>
                <tr><td>2</td><td>大气校正</td><td>利用自研辐射传输模型，得到全色或多光谱地表反射率影像</td></tr>
                <tr><td>3</td><td>几何校正</td><td>利用 RPC 文件，实现地表反射率图像的系统几何校正</td></tr>
                <tr><td>4</td><td>一站式处理</td><td>实现从辐射定标-大气校正-几何校正的一站式处理</td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td>根据经纬度信息，实现多景影像的拼接</td></tr>
                <tr><td>6</td><td>批处理</td><td>实现多景全色和多光谱影像的自动辐射校正批处理</td></tr>
              </tbody>
            </table>
          </div>

          <!-- 软件使用要求（GAC_AHSI_Mineral） -->
          <div class="software-requirements">
            <h3 class="subsection-title">软件使用要求</h3>
            <table class="features-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>功能名称</th>
                  <th>收费说明</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>辐射定标</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>2</td><td>大气校正</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>3</td><td>几何校正</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>4</td><td>一站式处理</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>5</td><td>图像镶嵌</td><td><span class="charge-paid">科研版，需缴费使用</span></td></tr>
                <tr><td>6</td><td>批处理</td><td><span class="charge-paid">企业版，需缴费使用</span></td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="section">
      <!-- 原有技术优势对比区 -->
      <!-- 原有技术优势对比区 -->
      <h2 class="section-title">技术优势对比</h2>
      <table class="comparison-table">
        <thead>
          <tr>
            <th>项目</th>
            <th>本软件</th>
            <th>传统方法（如6S、FLAASH）</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>大气参数获取</td>
            <td>自动匹配大气知识库</td>
            <td>人工设置或查找表</td>
          </tr>
          <tr>
            <td>精度控制</td>
            <td>动态计算大气模拟参数</td>
            <td>依赖经验/场景限定</td>
          </tr>
          <tr>
            <td>软件交互</td>
            <td>可视化界面 + 批处理</td>
            <td>命令行或脚本</td>
          </tr>
          <tr>
            <td>运行效率</td>
            <td>支持多线程/并行计算</td>
            <td>处理速度较慢</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="section">
      <h2 class="section-title">应用场景</h2>
      <div class="scene-grid">
        <div class="scene-card" v-for="(scene, i) in scenes" :key="i">
          <img :src="require(`@/assets/images/5/${i + 1}.png`)" alt="应用图" />
          <p>{{ scene }}</p>
        </div>
      </div>
    </div>

    <!-- <div class="section">
      <h2 class="section-title">产品优势</h2>
      <ul class="advantage-list">
        <li v-for="(adv, i) in advantages" :key="i">✔️ {{ adv }}</li>
      </ul>
    </div> -->

    <div class="section">
      <h2 class="section-title">使用人员</h2>
      <ul class="user-list">
        <li v-for="(user, i) in users" :key="i">👨‍🔬 {{ user }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Introduction',
  data() {
    return {
      currentSoftware: 1, // 默认显示第一个软件
      softwareList: [
        { id: 1, name: 'GAC_MSS' },
        { id: 2, name: 'GAC_PMS' },
        { id: 3, name: 'GAC_CCD' },
        { id: 4, name: 'GAC_AHSI_Water' },
        { id: 5, name: 'GAC_AHSI_Mineral' }
      ],
      features: [
        { 
          title: '自动化处理流程', 
          desc: '自动解析影像元数据（如观测角、太阳角），自动调用卫星载荷信息与大气参数，无需人工干预' 
        },
        { 
          title: '支持多种传感器', 
          desc: '高分系列、资源系列、中巴资源、环境等常见国产多光谱载荷，后续可扩展支持商业卫星（如吉林一号、欧比特、北京一号）' 
        },
        { 
          title: '精准的大气校正能力', 
          desc: '基于先进辐射传输模型正演建模，不依赖查找表，适用于复杂地形与大气状况，支持多种大气模型（中纬度夏、热带等）和气溶胶模式' 
        }
      ],
      timeline: [
        { year: '2020 Q1', event: '项目立项，组建中科院遥感所核心算法团队' },
        { year: '2020 Q4', event: '完成大气校正核心模块开发，通过IEEE标准验证' },
        { year: '2021 Q2', event: '集成机器学习模块，支持智能场景识别' },
        { year: '2021 Q4', event: '获得国家遥感中心认证，性能评测TOP 1' },
        { year: '2022 Q3', event: 'v1.0正式发布，服务100+科研院所' },
        { year: '2023 Q1', event: '推出企业版，支持PB级数据处理' },
      ],
      scenes: [
        '生态环境监测',
        '水质监测和水资源管理',
        '农业监测和作物估产',
        '城市规划和土地利用',
        '地质调查和矿物勘探'
      ],
      advantages: [
        '亚像元级处理精度，通过ISO-19130认证',
        '支持100+卫星传感器数据格式',
        '可视化工作流设计，一键生成专业报告',
        'GPU加速计算，提升大规模数据处理效率',
        '符合GB/T 33453-2016遥感数据处理标准'
      ],
      users: [
        '科研院所和高校科研人员',
        '自然资源管理技术人员',
        '生态环境监测技术人员',
        '农业遥感应用技术人员',
        '水资源管理工程师',
        '林草监测广利技术人员',
        '城市规划管理技术人员',
        '卫星遥感应用运营商'
      ],
    }
  },
  methods: {
    switchSoftware(softwareId) {
      this.currentSoftware = softwareId;
    }
  }
}
</script>

<style scoped>
:root {
  --bg-dark: #0A0A0F;
  --bg-darker: #050507;
  --primary: #00B4FF;
  --secondary: #00F7FF;
  --accent: #FF00FF;
  --text-primary: #E0E0FF;
  --text-secondary: #B0B0FF;
  --card-bg: rgba(10, 10, 15, 0.8);
  --quantum-glow: linear-gradient(135deg, var(--primary), var(--secondary));
}

.introduction-container {
  background: var(--bg-dark);
  color: var(--text-primary);
  padding: 2rem;
  max-width: 1400px;
  margin: auto;
  position: relative;
  overflow: hidden;
}

.main-content {
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
}

.software-sidebar {
  flex: 0 0 250px;
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
  height: fit-content;
  position: sticky;
  top: 2rem;
}

.sidebar-title {
  color: var(--primary);
  font-size: 1.2rem;
  margin-bottom: 1rem;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.software-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.software-btn {
  background: rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  color: var(--text-secondary);
  padding: 0.8rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  font-size: 0.9rem;
}

.software-btn:hover {
  background: rgba(0, 180, 255, 0.2);
  border-color: var(--primary);
  color: var(--text-primary);
  transform: translateX(5px);
}

.software-btn.active {
  background: var(--quantum-glow);
  border-color: var(--primary);
  color: var(--text-primary);
  box-shadow: 0 0 15px rgba(0, 180, 255, 0.3);
}

.content-area {
  flex: 1;
  background: var(--card-bg);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
}

.software-content {
  min-height: 600px;
}

.subsection-title {
  color: var(--primary);
  font-size: 1.3rem;
  margin: 2rem 0 1rem 0;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
  border-bottom: 2px solid rgba(0, 180, 255, 0.3);
  padding-bottom: 0.5rem;
}

.features-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  background: var(--card-bg);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
}

.features-table th,
.features-table td {
  padding: 1rem;
  text-align: left;
  border: 1px solid rgba(0, 180, 255, 0.2);
}

/* 收费说明样式区分 */
.charge-free { color: #27c93f; font-weight: 600; }
.charge-paid { color: #ffb020; font-weight: 600; }

.app-summary {
  color: var(--text-secondary);
  margin: 0.5rem 0 0 0;
}

.features-table th {
  background: rgba(0, 180, 255, 0.1);
  color: var(--primary);
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.features-table tr:nth-child(even) {
  background: rgba(0, 180, 255, 0.05);
}

.features-table tr:hover {
  background: rgba(0, 180, 255, 0.1);
}

.features-table td:first-child {
  font-weight: bold;
  color: var(--primary);
  width: 20%;
}

.requirements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 1rem 0;
}

.requirement-item {
  background: rgba(0, 180, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 180, 255, 0.2);
}

.requirement-item h4 {
  color: var(--primary);
  margin-bottom: 1rem;
  font-size: 1.1rem;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.requirement-item ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.requirement-item li {
  color: var(--text-secondary);
  padding: 0.5rem 0;
  padding-left: 1.5rem;
  position: relative;
  font-size: 0.9rem;
}

.requirement-item li::before {
  content: '▶';
  position: absolute;
  left: 0;
  color: var(--primary);
  font-size: 0.8rem;
  top: 0.5rem;
}

.introduction-container::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 50% 50%, rgba(0, 180, 255, 0.1) 0%, transparent 50%),
    linear-gradient(45deg, rgba(0, 247, 255, 0.05) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255, 0, 255, 0.05) 25%, transparent 25%);
  background-size: 100% 100%, 60px 60px, 60px 60px;
  background-position: center center, 0 0, 0 0;
  pointer-events: none;
  z-index: 0;
}

.main-title {
  text-align: center;
  color: var(--primary);
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
  position: relative;
}

.sub-title {
  text-align: center;
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  line-height: 1.6;
}

.highlight {
  color: var(--primary);
  font-weight: bold;
  text-shadow: 0 0 8px rgba(0, 180, 255, 0.3);
}


.section {
  margin: 2rem 0;
  background: var(--card-bg);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
}

.section-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: var(--primary);
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.timeline {
  list-style: none;
  padding: 0;
  border-left: 2px solid var(--primary);
  margin-left: 1rem;
  position: relative;
}

.timeline::before {
  content: '';
  position: absolute;
  top: 0;
  left: -2px;
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, var(--primary), var(--secondary));
  filter: blur(1px);
}

.timeline li {
  margin: 1.5rem 0;
  position: relative;
  padding-left: 1.5rem;
}

.timeline li::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 6px;
  width: 12px;
  height: 12px;
  background: var(--primary);
  border-radius: 50%;
  box-shadow: 0 0 10px var(--primary);
  transition: all 0.3s ease;
}

.timeline li:hover::before {
  transform: scale(1.2);
  box-shadow: 0 0 15px var(--primary);
}

.time {
  color: var(--primary);
  font-weight: bold;
  margin-right: 1rem;
}

.event {
  color: var(--text-secondary);
}

.scene-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.scene-card {
  background: var(--card-bg);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
  transition: all 0.3s ease;
}

.scene-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 0 30px rgba(0, 180, 255, 0.2);
  border-color: var(--primary);
}

.scene-card img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  filter: brightness(0.8) contrast(1.2);
}

.scene-card p {
  padding: 1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  text-align: center;
}

.advantage-list,
.user-list {
  list-style: none;
  padding: 0;
}

.advantage-list li,
.user-list li {
  margin: 1rem 0;
  padding: 0.8rem 1rem;
  background: rgba(0, 180, 255, 0.1);
  border-radius: 6px;
  color: var(--text-secondary);
  border: 1px solid rgba(0, 180, 255, 0.2);
  transition: all 0.3s ease;
}

.advantage-list li:hover,
.user-list li:hover {
  background: rgba(0, 180, 255, 0.15);
  border-color: var(--primary);
  transform: translateX(5px);
}

/* 添加量子粒子效果 */
.quantum-particles {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
}

.particle {
  position: absolute;
  width: 2px;
  height: 2px;
  background: var(--primary);
  border-radius: 50%;
  animation: particleFloat 20s infinite linear;
}

@keyframes particleFloat {
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: translateY(-100vh) translateX(100px);
    opacity: 0;
  }
}

/* 添加数据流动画 */
@keyframes dataFlow {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
}

.data-flow {
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, 
    transparent 0%,
    rgba(0, 180, 255, 0.1) 25%,
    transparent 50%,
    rgba(0, 247, 255, 0.1) 75%,
    transparent 100%
  );
  background-size: 200% 200%;
  animation: dataFlow 10s linear infinite;
  pointer-events: none;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  background: var(--card-bg);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
  border: 1px solid rgba(0, 180, 255, 0.2);
}

.comparison-table th,
.comparison-table td {
  padding: 1rem;
  text-align: left;
  border: 1px solid rgba(0, 180, 255, 0.2);
}

.comparison-table th {
  background: rgba(0, 180, 255, 0.1);
  color: var(--primary);
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
}

.comparison-table tr:nth-child(even) {
  background: rgba(0, 180, 255, 0.05);
}

.comparison-table tr:hover {
  background: rgba(0, 180, 255, 0.1);
}

.comparison-table td:first-child {
  font-weight: bold;
  color: var(--primary);
}

.comparison-table td:nth-child(2) {
  color: var(--text-primary);
}

.comparison-table td:nth-child(3) {
  color: var(--text-secondary);
}

.satellite-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  background: var(--card-bg);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.08);
  border: 1px solid rgba(0, 180, 255, 0.15);
}
.satellite-table th,
.satellite-table td {
  padding: 0.7rem 0.5rem;
  text-align: left;
  border: 1px solid rgba(0, 180, 255, 0.12);
  font-size: 0.98em;
}
.satellite-table th {
  background: rgba(0, 180, 255, 0.08);
  color: var(--primary);
  font-weight: bold;
}
.satellite-table tr:nth-child(even) {
  background: rgba(0, 180, 255, 0.03);
}
.satellite-table tr:hover {
  background: rgba(0, 180, 255, 0.08);
}
.satellite-catalog h2.section-title {
  color: var(--primary);
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.3);
}
.satellite-catalog h3 {
  color: #1a7f37;
  font-size: 1.15rem;
  margin-bottom: 0.7rem;
}
</style>
